import React, { useCallback } from 'react'

import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import Spinner from '@/components/common/Spinner'
import { FormInput } from '@/components/common/TextInputV2'
import Label from '@/components/form/Label'

import { ensureAbsoluteUrl } from '@/helpers/url'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useCreateOrganizationFileFromWebsite } from '@/hooks/organization/useCreateOrganizationFileFromWebsite'

import type { UUID } from '@/types/Id'

interface CreateFromWebsiteDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  onSuccess?: (fileId: UUID) => void
}

interface CreateFromWebsiteFormValues {
  url: string
}

const DEFAULT_VALUES: CreateFromWebsiteFormValues = {
  url: '',
}

const WEBSITE_URL_FIELD_ID = 'create-from-website-url'
const WEBSITE_URL_REQUIRED_MESSAGE = 'Website address is required'
const WEBSITE_URL_INVALID_MESSAGE = 'Enter a valid public website address'

const IPV4_REGEX = /^\d{1,3}(?:\.\d{1,3}){3}$/

const getNormalizedWebsiteUrl = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }

  return ensureAbsoluteUrl(trimmed)
}

const getWebsiteUrlValidationMessage = (value: string): string | undefined => {
  const normalized = getNormalizedWebsiteUrl(value)

  if (!normalized) {
    return WEBSITE_URL_REQUIRED_MESSAGE
  }

  try {
    const url = new URL(normalized)
    const hostname = url.hostname.trim().toLowerCase()

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return WEBSITE_URL_INVALID_MESSAGE
    }

    if (url.username || url.password || !hostname) {
      return WEBSITE_URL_INVALID_MESSAGE
    }

    if (
      IPV4_REGEX.test(hostname) &&
      (hostname.startsWith('127.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('0.') ||
        hostname.startsWith('169.254.') ||
        hostname.startsWith('192.168.') ||
        (() => {
          const [first, second] = hostname.split('.')
          return first === '172' && Number(second) >= 16 && Number(second) <= 31
        })())
    ) {
      return WEBSITE_URL_INVALID_MESSAGE
    }

    if (
      hostname === 'localhost' ||
      hostname.endsWith('.local') ||
      (!hostname.includes('.') && !IPV4_REGEX.test(hostname))
    ) {
      return WEBSITE_URL_INVALID_MESSAGE
    }

    return undefined
  } catch {
    return WEBSITE_URL_INVALID_MESSAGE
  }
}

export const CreateFromWebsiteDialog: React.FC<
  CreateFromWebsiteDialogProps
> = ({ open, setOpen, onSuccess }) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutateAsync: createFromWebsite, isPending } =
    useCreateOrganizationFileFromWebsite({
      orgId: activeOrgId,
    })

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    formState: { errors, isValid, isValidating },
  } = useForm<CreateFromWebsiteFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  })
  const urlValue = watch('url')
  const urlField = register('url', {
    required: WEBSITE_URL_REQUIRED_MESSAGE,
    validate: (value) => getWebsiteUrlValidationMessage(value) ?? true,
  })

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)

      if (!nextOpen) {
        reset(DEFAULT_VALUES)
      }
    },
    [reset, setOpen],
  )

  const handleCreate = useCallback(
    async (data: CreateFromWebsiteFormValues) => {
      try {
        const normalizedUrl = getNormalizedWebsiteUrl(data.url)
        const response = await createFromWebsite({
          url: normalizedUrl,
          unrestricted: true,
          tag_ids: [],
        })

        handleClose(false)
        onSuccess?.(response.file_id)
        toast.success(
          'Website import started. The PDF will appear in your library once it is ready.',
        )
      } catch {
        // Error is handled by the mutation hook.
      }
    },
    [createFromWebsite, handleClose, onSuccess],
  )

  const handleUrlBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const normalizedUrl = getNormalizedWebsiteUrl(event.target.value)

      if (normalizedUrl !== event.target.value) {
        setValue('url', normalizedUrl, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }

      urlField.onBlur(event)
    },
    [setValue, urlField],
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Import Website</DialogTitle>
          <DialogDescription>
            Render a public webpage as a PDF and add it to your library. You can
            paste a full page URL or just a domain.
          </DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={handleSubmit(handleCreate)}>
          <div className="flex flex-col gap-5 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={WEBSITE_URL_FIELD_ID} required>
                Website address
              </Label>
              <FormInput
                {...urlField}
                id={WEBSITE_URL_FIELD_ID}
                type="url"
                inputMode="url"
                autoComplete="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                error={errors.url?.message}
                onBlur={handleUrlBlur}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-end mt-2">
            <DialogClose asChild>
              <Button
                variant="outline-compact"
                type="button"
                disabled={isPending}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary-compact"
              type="submit"
              disabled={
                isPending ||
                isValidating ||
                !urlValue.trim() ||
                (!isValid && !!errors.url?.message)
              }
            >
              {isPending ? (
                <>
                  <Spinner variant="primary-compact" className="w-4 h-4" />
                  <span>Starting...</span>
                </>
              ) : (
                'Import Website'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
